// P4 sheets (frozen mockups-pur-v1): SCR-PUR-03 purchase (order or direct receipt), -04 receive against an order,
// -05 close remainder and cancel, -06 supplier return. The database decides (balances, remaining, average cost);
// these checks only give instant feedback. The Storekeeper never sees an amount (GEN-15, ADR-099).
import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BottomSheet,
  Button,
  Chips,
  Field,
  FormGrid,
  Input,
  LineAdd,
  List,
  ListRow,
  Notice,
  Section,
  Select,
  SheetActions,
  Tabs,
} from '../../design/kit';
import { Refusal } from '../../data/auth';
import { CASH, loadBusinessCash } from '../../data/cash';
import {
  PURCHASING,
  cancelOrder,
  closeOrder,
  createDirectReceipt,
  createOrder,
  loadReceipts,
  receiveOrder,
  returnToSupplier,
  type Delivery,
  type LineUnit,
  type PickupCategory,
  type PickupInput,
  type Place,
  type PurchaseLineInput,
  type PurchaseOrder,
} from '../../data/purchasing';
import { STOCK, countWord } from '../stock/StockSheets';
import { useInventory } from '../stock/InventoryPage';
import { MoneyField } from '../cash/CashSheets';
import { Name } from '../../i18n/Isolate';
import type { Language } from '../../i18n';
import { messageKey } from '../../lib/errors';
import { businessToday, formatMoney } from '../../lib/format';
import { useSession } from '../../app/session';
import { CATALOG, parseAmount, useCatalogData, useSuppliers } from '../catalog/common';

type Problem = { field: string; text: string };
const PICKUP: PickupCategory[] = ['fuel', 'toll', 'meal', 'other'];
const RETURN_REASONS = ['rr.damaged', 'rr.expired', 'rr.wrong', 'rr.other'];

/** Amount above zero with at most two decimals, or null. */
function money(text: string): number | null {
  const v = parseAmount(text);
  return v !== null && v > 0 && Math.round(v * 100) === v * 100 ? v : null;
}
function whole(text: string): number | null {
  return /^\d+$/.test(text.trim()) ? Number(text.trim()) : null;
}
/** "2 boxes" or "5 pieces": a quantity in the line's unit. */
export function unitQty(t: TFunction, unit: LineUnit, n: number): string {
  return countWord(t, unit, n);
}

export function usePurchaseWords() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as Language;
  const fmt = (v: number | string) => formatMoney(v, lang);
  const refusal = (err: unknown): Problem => {
    if (!(err instanceof Refusal)) return { field: 'form', text: t(messageKey('SERVER_ERROR')) };
    const d = err.detail;
    const q = d.left !== undefined ? unitQty(t, (d.unit as LineUnit) ?? 'piece', Number(d.left)) : '';
    switch (err.code) {
      case 'INSUFFICIENT_CASH':
        return {
          field: 'pay',
          text:
            d.balance === undefined
              ? t('err.cashsk')
              : t('err.cashfunds', { w: t(`cash.${String(d.account)}`), a: fmt(String(d.balance)) }),
        };
      case 'ABOVE_REMAINING':
        return { field: 'lines', text: t('err.aboveleft', { q }) };
      case 'ABOVE_RETURNABLE':
        return { field: 'qty', text: t('err.abovereturn', { q }) };
      case 'INVALID_PRICE':
        return { field: 'line', text: t('err.unitprice') };
      case 'REASON_REQUIRED':
        return { field: 'reason', text: t('err.reason') };
      default:
        return { field: 'form', text: t(messageKey(err.code), { p: String(d.product ?? '') }) };
    }
  };
  return { fmt, refusal, lang };
}

function useDone(onClose: () => void) {
  const qc = useQueryClient();
  return () => {
    for (const key of [PURCHASING, STOCK, CASH, CATALOG]) void qc.invalidateQueries({ queryKey: key });
    onClose();
  };
}

/** The cash balances the owner pays from (the Storekeeper never asks, ADR-099). */
function useBalances(owner: boolean) {
  return useQuery({ queryKey: [...CASH, 'business'], queryFn: loadBusinessCash, enabled: owner });
}

function PlaceTabs({ value, onChange, label }: { value: Place; onChange: (p: Place) => void; label: string }) {
  const { t } = useTranslation();
  return (
    <Tabs
      label={label}
      value={value}
      onChange={(v) => onChange(v as Place)}
      items={[
        { id: 'garage', label: t('cash.garage') },
        { id: 'bank', label: t('cash.bank') },
      ]}
    />
  );
}

/** PUR-09 / ADR-098: how the goods arrive and the pickup-trip expense lines. */
function DeliveryFields({
  delivery,
  onDelivery,
  pickup,
  onPickup,
  error,
}: {
  delivery: Delivery;
  onDelivery: (d: Delivery) => void;
  pickup: PickupInput[];
  onPickup: (p: PickupInput[]) => void;
  error?: string | undefined;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as Language;
  const [category, setCategory] = useState<PickupCategory>('fuel');
  const [amount, setAmount] = useState('');
  const [problem, setProblem] = useState<string>();
  const add = () => {
    const v = money(amount);
    if (v === null) return setProblem(t('err.amount'));
    setProblem(undefined);
    onPickup([...pickup, { category, amount: v }]);
    setAmount('');
  };
  return (
    <>
      <Tabs
        label={t('delivery')}
        value={delivery}
        onChange={(v) => onDelivery(v as Delivery)}
        items={[
          { id: 'supplier', label: t('dl.supplier') },
          { id: 'pickup', label: t('dl.pickup') },
        ]}
      />
      {delivery === 'pickup' && (
        <Section title={t('pk.lines')}>
          {pickup.length > 0 && (
            <List label={t('pk.lines')} animateNew>
              {pickup.map((p, i) => (
                <ListRow
                  key={`${p.category}-${i}`}
                  testId={`pickup-${i}`}
                  lead={{ icon: p.category === 'fuel' ? 'truck' : 'file' }}
                  title={t(`pk.${p.category}`)}
                  value={formatMoney(p.amount, lang)}
                  actions={
                    <Button
                      pill
                      icon="close"
                      aria-label={t('remove')}
                      onClick={() => onPickup(pickup.filter((_, j) => j !== i))}
                    />
                  }
                />
              ))}
            </List>
          )}
          <LineAdd>
            <Chips
              label={t('pk.lines')}
              items={PICKUP.map((c) => ({ id: c, label: t(`pk.${c}`) }))}
              selected={[category]}
              onToggle={(c) => setCategory(c as PickupCategory)}
            />
            <MoneyField label={t('amount')} value={amount} onChange={setAmount} error={problem ?? error} />
            <Button icon="plus" onClick={add}>
              {t('pk.add')}
            </Button>
          </LineAdd>
          <p className="muted">{t('pk.hint')}</p>
        </Section>
      )}
    </>
  );
}

function Totals({ rows }: { rows: [string, string, boolean?][] }) {
  return (
    <List label={rows[rows.length - 1]?.[0] ?? ''}>
      {rows.map(([label, value, strong]) => (
        <ListRow key={label} title={strong ? <b>{label}</b> : label} value={strong ? <b>{value}</b> : value} />
      ))}
    </List>
  );
}

/** SCR-PUR-03: the owner's one purchase form — a purchase order or a direct receipt (PUR-01, PUR-05). */
export function PurchaseSheet({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { fmt, refusal } = usePurchaseWords();
  const done = useDone(onClose);
  const suppliers = useSuppliers();
  const { products } = useCatalogData();
  const balances = useBalances(true);
  const [kind, setKind] = useState<'order' | 'direct'>('order');
  const [supplier, setSupplier] = useState('');
  const [lines, setLines] = useState<PurchaseLineInput[]>([]);
  const [product, setProduct] = useState('');
  const [unit, setUnit] = useState<LineUnit>('box');
  const [qty, setQty] = useState('');
  const [price, setPrice] = useState('');
  const [lotNo, setLotNo] = useState('');
  const [expiry, setExpiry] = useState('');
  const [ref, setRef] = useState('');
  const [expected, setExpected] = useState('');
  const [date, setDate] = useState(businessToday());
  const [delivery, setDelivery] = useState<Delivery>('supplier');
  const [pickup, setPickup] = useState<PickupInput[]>([]);
  const [paidFrom, setPaidFrom] = useState<Place>('garage');
  const [problem, setProblem] = useState<Problem>();
  // PRD-08: a bundle is never bought.
  const active = (products.data ?? []).filter((p) => p.active && !p.is_bundle);
  const prod = active.find((p) => p.id === product);
  const byId = (id: string) => (products.data ?? []).find((p) => p.id === id);
  const needsLot = kind === 'direct' && Boolean(prod?.lot_tracked);
  const goods = lines.reduce((s, l) => s + l.quantity * l.price, 0);
  const trip = kind === 'direct' && delivery === 'pickup' ? pickup.reduce((s, p) => s + p.amount, 0) : 0;
  const available = balances.data ? Number(balances.data[paidFrom]) : null;
  const pieceHint =
    prod && unit === 'box' && prod.box_capacity && money(price) !== null
      ? t('pu.pricehint', { n: prod.box_capacity, p: fmt((money(price) ?? 0) / prod.box_capacity) })
      : undefined;

  const addLine = () => {
    const q = whole(qty);
    const p = money(price);
    if (!prod) return setProblem({ field: 'line', text: t('err.required') });
    if (unit === 'box' && prod.box_capacity === null) return setProblem({ field: 'line', text: t('err.boxloose') });
    if (q === null || q === 0) return setProblem({ field: 'line', text: t('err.qty') });
    if (p === null) return setProblem({ field: 'line', text: t('err.unitprice') });
    if (needsLot && !lotNo.trim()) return setProblem({ field: 'line', text: t('err.lotno') });
    if (needsLot && !expiry) return setProblem({ field: 'line', text: t('err.lotexp') });
    setProblem(undefined);
    setLines([
      ...lines,
      { product: prod.id, unit, quantity: q, price: p, ...(needsLot ? { lot: lotNo.trim(), expiry } : {}) },
    ]);
    setQty('');
    setPrice('');
    setLotNo('');
    setExpiry('');
  };
  const save = useMutation({
    mutationFn: () =>
      kind === 'order'
        ? createOrder(supplier, lines, ref.trim(), expected || null)
        : createDirectReceipt(supplier, lines, ref.trim(), date, delivery, pickup, paidFrom),
    onSuccess: done,
    onError: (e) => setProblem(refusal(e)),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!supplier) return setProblem({ field: 'supplier', text: t('err.nosupplier') });
    if (lines.length === 0) return setProblem({ field: 'line', text: t('err.nolines') });
    if (kind === 'order' && expected && expected < businessToday()) {
      return setProblem({ field: 'date', text: t('err.datepast') });
    }
    if (kind === 'direct' && date > businessToday()) return setProblem({ field: 'date', text: t('err.datefuture') });
    if (kind === 'direct' && available !== null && goods + trip > available) {
      return setProblem({
        field: 'pay',
        text: t('err.cashfunds', { w: t(`cash.${paidFrom}`), a: fmt(available) }),
      });
    }
    setProblem(undefined);
    save.mutate();
  };
  const err = (f: string) => (problem?.field === f ? problem.text : undefined);
  return (
    <BottomSheet title={t('s43.title')} open onClose={onClose} busy={save.isPending}>
      <form onSubmit={submit} noValidate className="yv-skeleton-stack">
        {problem?.field === 'form' && <Notice tone="danger">{problem.text}</Notice>}
        <Tabs
          label={t('s43.title')}
          value={kind}
          onChange={(v) => setKind(v as 'order' | 'direct')}
          items={[
            { id: 'order', label: t('kind.order') },
            { id: 'direct', label: t('kind.direct') },
          ]}
        />
        <Field label={t('supplier')} error={err('supplier')}>
          {({ id, describedBy, invalid }) => (
            <Select
              id={id}
              value={supplier}
              onChange={(e) => {
                setSupplier(e.target.value);
                setLines([]);
              }}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
            >
              <option value="">{t('choose')}</option>
              {(suppliers.data ?? [])
                .filter((s) => s.active)
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
            </Select>
          )}
        </Field>
        <LineAdd>
          <Field label={t('product')}>
            {({ id }) => (
              <Select
                id={id}
                value={product}
                onChange={(e) => {
                  setProduct(e.target.value);
                  const p = active.find((x) => x.id === e.target.value);
                  setUnit(p?.box_capacity ? 'box' : 'piece');
                }}
              >
                <option value="">{t('choose')}</option>
                {active.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {prod?.box_capacity && (
            <Tabs
              label={t('unit')}
              value={unit}
              onChange={(v) => setUnit(v as LineUnit)}
              items={[
                { id: 'box', label: t('unit.boxline') },
                { id: 'piece', label: t('unit.pieceline') },
              ]}
            />
          )}
          <FormGrid>
            <Field label={t('quantity')}>
              {({ id }) => (
                <Input id={id} numeric inputMode="numeric" value={qty} onChange={(e) => setQty(e.target.value)} />
              )}
            </Field>
            <MoneyField label={t('pu.price')} value={price} onChange={setPrice} hint={pieceHint} />
          </FormGrid>
          {needsLot && (
            <FormGrid>
              <Field label={t('lot.no')}>
                {({ id }) => <Input id={id} dir="ltr" value={lotNo} onChange={(e) => setLotNo(e.target.value)} />}
              </Field>
              <Field label={t('lot.expiry')}>
                {({ id }) => (
                  <Input id={id} type="date" dir="ltr" value={expiry} onChange={(e) => setExpiry(e.target.value)} />
                )}
              </Field>
            </FormGrid>
          )}
          {err('line') && <Notice tone="danger">{err('line')}</Notice>}
          <Button icon="plus" onClick={addLine}>
            {t('line.add')}
          </Button>
        </LineAdd>
        {lines.length > 0 && (
          <List label={t('tab.lines')}>
            {lines.map((l, i) => (
              <ListRow
                key={`${l.product}-${l.unit}-${i}`}
                testId={`pline-${i}`}
                title={<Name>{byId(l.product)?.name ?? ''}</Name>}
                sub={`${unitQty(t, l.unit, l.quantity)} × ${fmt(l.price)}${l.lot ? ` · ${l.lot}` : ''}`}
                value={fmt(l.quantity * l.price)}
                actions={
                  <Button
                    pill
                    icon="close"
                    aria-label={t('remove')}
                    onClick={() => setLines(lines.filter((_, j) => j !== i))}
                  />
                }
              />
            ))}
          </List>
        )}
        <Field label={t('pu.ref')}>
          {({ id }) => <Input id={id} dir="ltr" value={ref} onChange={(e) => setRef(e.target.value)} />}
        </Field>
        {kind === 'order' ? (
          <Field label={t('pu.expdate')} error={err('date')}>
            {({ id, describedBy }) => (
              <Input
                id={id}
                type="date"
                dir="ltr"
                min={businessToday()}
                value={expected}
                onChange={(e) => setExpected(e.target.value)}
                aria-describedby={describedBy}
              />
            )}
          </Field>
        ) : (
          <>
            <Field label={t('pu.recdate')} error={err('date')}>
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  type="date"
                  dir="ltr"
                  max={businessToday()}
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  aria-describedby={describedBy}
                />
              )}
            </Field>
            <DeliveryFields delivery={delivery} onDelivery={setDelivery} pickup={pickup} onPickup={setPickup} />
            <Field
              label={t('paidfrom')}
              hint={available !== null ? t('available', { a: fmt(available) }) : undefined}
              error={err('pay')}
            >
              {() => <PlaceTabs value={paidFrom} onChange={setPaidFrom} label={t('paidfrom')} />}
            </Field>
          </>
        )}
        <Totals
          rows={
            kind === 'order'
              ? [[t('pu.total'), fmt(goods), true]]
              : [
                  [t('pu.goods'), fmt(goods)],
                  [t('pu.pickupcost'), fmt(trip)],
                  [t('pu.paynow'), fmt(goods + trip), true],
                ]
          }
        />
        <SheetActions>
          <Button type="submit" variant="main" busy={save.isPending}>
            {kind === 'order' ? t('pu.saveorder') : t('pu.receivepay')}
          </Button>
          <Button onClick={onClose}>{t('cancel')}</Button>
        </SheetActions>
      </form>
    </BottomSheet>
  );
}

/** SCR-PUR-04: receive against an order, partial allowed, all lines together (PUR-04, ADR-099). */
export function ReceiveSheet({ order, onClose }: { order: PurchaseOrder; onClose: () => void }) {
  const { t } = useTranslation();
  const { role } = useSession();
  const owner = role === 'owner';
  const { fmt, refusal } = usePurchaseWords();
  const done = useDone(onClose);
  const balances = useBalances(owner);
  const open = order.lines.filter((l) => l.received < l.quantity);
  const [qty, setQty] = useState<Record<number, string>>(() =>
    Object.fromEntries(open.map((l) => [l.id, String(l.quantity - l.received)])),
  );
  // PRD-09: lot-tracked lines need their lot and an expiry after today.
  const { products } = useCatalogData();
  const tracked = (l: { product_id: string }) =>
    (products.data ?? []).some((p) => p.id === l.product_id && p.lot_tracked);
  const [lots, setLots] = useState<Record<number, { lot: string; expiry: string }>>({});
  const lotOf = (id: number) => lots[id] ?? { lot: '', expiry: '' };
  const [delivery, setDelivery] = useState<Delivery>('supplier');
  const [pickup, setPickup] = useState<PickupInput[]>([]);
  const [paidFrom, setPaidFrom] = useState<Place>('garage');
  const [problem, setProblem] = useState<Problem & { line?: number }>();
  const amount =
    open.reduce((s, l) => s + (whole(qty[l.id] ?? '') ?? 0) * (l.unit_price ?? 0), 0) +
    (delivery === 'pickup' ? pickup.reduce((s, p) => s + p.amount, 0) : 0);
  const available = balances.data ? Number(balances.data[paidFrom]) : null;
  const save = useMutation({
    mutationFn: () =>
      receiveOrder(
        order.id,
        open.map((l) => ({
          line: l.id,
          quantity: whole(qty[l.id] ?? '') ?? 0,
          ...(tracked(l) ? { lot: lotOf(l.id).lot.trim(), expiry: lotOf(l.id).expiry } : {}),
        })),
        delivery,
        pickup,
        paidFrom,
      ),
    onSuccess: done,
    onError: (e) => setProblem(refusal(e)),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    for (const l of open) {
      const q = whole(qty[l.id] ?? '');
      if (q === null) return setProblem({ field: 'lines', line: l.id, text: t('err.qty') });
      if (q > l.quantity - l.received) {
        return setProblem({
          field: 'lines',
          line: l.id,
          text: t('err.aboveleft', { q: unitQty(t, l.unit, l.quantity - l.received) }),
        });
      }
      if (q > 0 && tracked(l) && !lotOf(l.id).lot.trim())
        return setProblem({ field: 'lines', line: l.id, text: t('err.lotno') });
      if (q > 0 && tracked(l) && !lotOf(l.id).expiry)
        return setProblem({ field: 'lines', line: l.id, text: t('err.lotexp') });
    }
    if (open.every((l) => (whole(qty[l.id] ?? '') ?? 0) === 0))
      return setProblem({ field: 'lines', text: t('err.nothing') });
    if (owner && available !== null && amount > available) {
      return setProblem({ field: 'pay', text: t('err.cashfunds', { w: t(`cash.${paidFrom}`), a: fmt(available) }) });
    }
    setProblem(undefined);
    save.mutate();
  };
  return (
    <BottomSheet title={`${t('s44.title')} · ${order.number}`} open onClose={onClose} busy={save.isPending}>
      <form onSubmit={submit} noValidate className="yv-skeleton-stack">
        {problem &&
          (problem.field === 'form' ||
            (problem.field === 'lines' && !problem.line) ||
            (!owner && problem.field === 'pay')) && <Notice tone="danger">{problem.text}</Notice>}
        <List label={t('tab.lines')}>
          {order.lines.map((l) => {
            const left = l.quantity - l.received;
            return (
              <ListRow
                key={l.id}
                testId={`recv-${l.sku ?? l.id}`}
                title={<Name>{l.product}</Name>}
                sub={
                  <>
                    {t('rc.ordered')} {unitQty(t, l.unit, l.quantity)} · {t('rc.done')} {unitQty(t, l.unit, l.received)}{' '}
                    · {t('rc.left')} {unitQty(t, l.unit, left)}
                    {left > 0 && tracked(l) && (
                      <FormGrid>
                        <Field label={t('lot.no')}>
                          {({ id }) => (
                            <Input
                              id={id}
                              dir="ltr"
                              value={lotOf(l.id).lot}
                              onChange={(e) => setLots({ ...lots, [l.id]: { ...lotOf(l.id), lot: e.target.value } })}
                            />
                          )}
                        </Field>
                        <Field label={t('lot.expiry')}>
                          {({ id }) => (
                            <Input
                              id={id}
                              type="date"
                              dir="ltr"
                              value={lotOf(l.id).expiry}
                              onChange={(e) => setLots({ ...lots, [l.id]: { ...lotOf(l.id), expiry: e.target.value } })}
                            />
                          )}
                        </Field>
                      </FormGrid>
                    )}
                    {problem?.line === l.id && (
                      <span className="yv-field__error" role="alert">
                        {problem.text}
                      </span>
                    )}
                  </>
                }
                actions={
                  left > 0 ? (
                    <label className="yv-unit-input yv-unit-input--narrow">
                      <Input
                        numeric
                        inputMode="numeric"
                        aria-label={`${t('rc.now')} · ${l.product}`}
                        value={qty[l.id] ?? ''}
                        onChange={(e) => setQty({ ...qty, [l.id]: e.target.value })}
                        aria-invalid={problem?.line === l.id || undefined}
                      />
                      <span className="yv-unit-input__unit" aria-hidden="true">
                        {t(l.unit === 'box' ? 'qty.boxes' : 'qty.pieces')}
                      </span>
                    </label>
                  ) : undefined
                }
                value={left > 0 ? undefined : t('rc.complete')}
              />
            );
          })}
        </List>
        <DeliveryFields delivery={delivery} onDelivery={setDelivery} pickup={pickup} onPickup={setPickup} />
        {owner ? (
          <>
            <Field
              label={t('paidfrom')}
              hint={available !== null ? t('available', { a: fmt(available) }) : undefined}
              error={problem?.field === 'pay' ? problem.text : undefined}
            >
              {() => <PlaceTabs value={paidFrom} onChange={setPaidFrom} label={t('paidfrom')} />}
            </Field>
            <Totals rows={[[t('rc.topay'), fmt(amount), true]]} />
          </>
        ) : (
          <Notice tone="info">{t('rc.skhint')}</Notice>
        )}
        <SheetActions>
          <Button type="submit" variant="main" busy={save.isPending}>
            {t('rc.save')}
          </Button>
          <Button onClick={onClose}>{t('cancel')}</Button>
        </SheetActions>
      </form>
    </BottomSheet>
  );
}

/** SCR-PUR-05: close a partly received order's remainder, with a reason (owner, ADR-097). */
export function CloseSheet({ order, onClose }: { order: PurchaseOrder; onClose: () => void }) {
  const { t } = useTranslation();
  const { refusal } = usePurchaseWords();
  const done = useDone(onClose);
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<Problem>();
  const save = useMutation({
    mutationFn: () => closeOrder(order.id, reason.trim()),
    onSuccess: done,
    onError: (e) => setProblem(refusal(e)),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) return setProblem({ field: 'reason', text: t('err.reason') });
    save.mutate();
  };
  return (
    <BottomSheet title={t('close.title')} open onClose={onClose} busy={save.isPending}>
      <form onSubmit={submit} noValidate className="yv-skeleton-stack">
        {problem?.field === 'form' && <Notice tone="danger">{problem.text}</Notice>}
        <Notice tone="info">{t('close.hint')}</Notice>
        <List label={t('rc.left')}>
          {order.lines
            .filter((l) => l.received < l.quantity)
            .map((l) => (
              <ListRow
                key={l.id}
                title={<Name>{l.product}</Name>}
                sub={`${t('rc.left')} ${unitQty(t, l.unit, l.quantity - l.received)}`}
              />
            ))}
        </List>
        <Field label={t('reason')} error={problem?.field === 'reason' ? problem.text : undefined}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              dir="auto"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
            />
          )}
        </Field>
        <SheetActions>
          <Button type="submit" variant="main" busy={save.isPending}>
            {t('close.save')}
          </Button>
          <Button onClick={onClose}>{t('cancel')}</Button>
        </SheetActions>
      </form>
    </BottomSheet>
  );
}

/** SCR-PUR-05: cancel an order with no receipt; the confirmation lives in the sheet (owner, ADR-097). */
export function CancelSheet({ order, onClose }: { order: PurchaseOrder; onClose: () => void }) {
  const { t } = useTranslation();
  const { refusal } = usePurchaseWords();
  const done = useDone(onClose);
  const [problem, setProblem] = useState<Problem>();
  const save = useMutation({
    mutationFn: () => cancelOrder(order.id),
    onSuccess: done,
    onError: (e) => setProblem(refusal(e)),
  });
  return (
    <BottomSheet title={`${t('cancel.title')} · ${order.number}`} open onClose={onClose} busy={save.isPending}>
      <div className="yv-skeleton-stack">
        {problem && <Notice tone="danger">{problem.text}</Notice>}
        <p>{t('cancel.text')}</p>
        <SheetActions>
          <Button variant="danger" busy={save.isPending} onClick={() => save.mutate()}>
            {t('cancel.save')}
          </Button>
          <Button onClick={onClose}>{t('cancel')}</Button>
        </SheetActions>
      </div>
    </BottomSheet>
  );
}

/** SCR-PUR-06: return goods of an original receipt line to its supplier (PUR-07, ADR-099). */
export function ReturnSheet({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { role } = useSession();
  const owner = role === 'owner';
  const { fmt, refusal } = usePurchaseWords();
  const done = useDone(onClose);
  const receipts = useQuery({ queryKey: [...PURCHASING, 'receipts'], queryFn: () => loadReceipts() });
  const inventory = useInventory();
  const { costs } = useCatalogData();
  const [supplier, setSupplier] = useState('');
  const [receipt, setReceipt] = useState('');
  const [source, setSource] = useState<'garage' | 'damaged'>('garage');
  const [lineId, setLineId] = useState('');
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState('');
  const [refundTo, setRefundTo] = useState<Place>('garage');
  const [problem, setProblem] = useState<Problem>();
  const suppliers = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of receipts.data ?? []) seen.set(r.supplier_id, r.supplier);
    return [...seen.entries()];
  }, [receipts.data]);
  const theirs = (receipts.data ?? []).filter((r) => r.supplier_id === supplier);
  const rec = theirs.find((r) => r.id === receipt);
  const line = rec?.lines.find((l) => String(l.id) === lineId);
  const returnable = line ? line.quantity - line.returned : 0;
  const stock = line
    ? (inventory.data ?? []).find((r) => r.product_id === line.product_id && r.location === source)
    : undefined;
  const held = stock ? (line?.unit === 'box' ? stock.boxes : stock.loose) : 0;
  const q = whole(qty);
  const refund = line && q ? q * (line.unit_price ?? 0) : 0;
  const avg = line ? costs.data?.get(line.product_id)?.avg_cost : null;
  const pieces = line && q ? q * (line.unit === 'box' ? (line.box_capacity ?? 1) : 1) : 0;
  const save = useMutation({
    mutationFn: () => returnToSupplier(line?.id ?? 0, source, q ?? 0, reason, refundTo),
    onSuccess: done,
    onError: (e) => setProblem(refusal(e)),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!line) return setProblem({ field: 'line', text: t('err.receiptreq') });
    if (q === null || q === 0) return setProblem({ field: 'qty', text: t('err.qty') });
    if (q > returnable)
      return setProblem({ field: 'qty', text: t('err.abovereturn', { q: unitQty(t, line.unit, returnable) }) });
    if (q > held) return setProblem({ field: 'qty', text: t('err.overbalance', { b: unitQty(t, line.unit, held) }) });
    if (!reason) return setProblem({ field: 'reason', text: t('err.reason') });
    setProblem(undefined);
    save.mutate();
  };
  const err = (f: string) => (problem?.field === f ? problem.text : undefined);
  return (
    <BottomSheet title={t('s46.title')} open onClose={onClose} busy={save.isPending}>
      <form onSubmit={submit} noValidate className="yv-skeleton-stack">
        {problem?.field === 'form' && <Notice tone="danger">{problem.text}</Notice>}
        <Field label={t('supplier')}>
          {({ id }) => (
            <Select
              id={id}
              value={supplier}
              onChange={(e) => {
                setSupplier(e.target.value);
                setReceipt('');
                setLineId('');
              }}
            >
              <option value="">{t('choose')}</option>
              {suppliers.map(([sid, name]) => (
                <option key={sid} value={sid}>
                  {name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t('rt.receipt')} error={err('line')}>
          {({ id, describedBy }) => (
            <Select
              id={id}
              value={receipt}
              onChange={(e) => {
                setReceipt(e.target.value);
                setLineId('');
              }}
              aria-describedby={describedBy}
            >
              <option value="">{t('choose')}</option>
              {theirs.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.number} · {r.business_date}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Tabs
          label={t('rt.source')}
          value={source}
          onChange={(v) => setSource(v as 'garage' | 'damaged')}
          items={[
            { id: 'garage', label: t('loc.garage') },
            { id: 'damaged', label: t('loc.damaged') },
          ]}
        />
        <Field
          label={t('product')}
          hint={
            line
              ? `${t('rt.returnable', { q: unitQty(t, line.unit, returnable) })} · ${t('rt.balance', { w: t(`loc.${source}`), q: unitQty(t, line.unit, held) })}`
              : undefined
          }
        >
          {({ id, describedBy }) => (
            <Select id={id} value={lineId} onChange={(e) => setLineId(e.target.value)} aria-describedby={describedBy}>
              <option value="">{t('choose')}</option>
              {(rec?.lines ?? [])
                .filter((l) => l.quantity > l.returned)
                .map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.product} — {unitQty(t, l.unit, l.quantity - l.returned)}
                  </option>
                ))}
            </Select>
          )}
        </Field>
        <Field label={t('quantity')} error={err('qty')}>
          {({ id, describedBy, invalid }) => (
            <label className="yv-unit-input">
              <Input
                id={id}
                numeric
                inputMode="numeric"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
              />
              <span className="yv-unit-input__unit" aria-hidden="true">
                {t(line?.unit === 'piece' ? 'qty.pieces' : 'qty.boxes')}
              </span>
            </label>
          )}
        </Field>
        <Field label={t('reason')} error={err('reason')}>
          {() => (
            <Chips
              label={t('reason')}
              items={RETURN_REASONS.map((k) => ({ id: k, label: t(k) }))}
              selected={RETURN_REASONS.filter((k) => t(k) === reason)}
              onToggle={(k) => setReason(t(k))}
            />
          )}
        </Field>
        {owner ? (
          <>
            <Field label={t('rt.refundto')}>
              {() => <PlaceTabs value={refundTo} onChange={setRefundTo} label={t('rt.refundto')} />}
            </Field>
            <Totals rows={[[t('rt.refund'), fmt(refund), true]]} />
            {avg !== null && avg !== undefined && q ? (
              <p className="muted">{t('rt.diff', { a: fmt(Math.round(pieces * avg * 100) / 100 - refund) })}</p>
            ) : null}
          </>
        ) : (
          <Notice tone="info">{t('rc.skhint')}</Notice>
        )}
        <SheetActions>
          <Button type="submit" variant="main" busy={save.isPending}>
            {t('rt.save')}
          </Button>
          <Button onClick={onClose}>{t('cancel')}</Button>
        </SheetActions>
      </form>
    </BottomSheet>
  );
}
